import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Tv, Search, FileStack, Trophy, FolderArchive, Map, Radio, X, BadgeCheck, GraduationCap, ShieldCheck } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { api, formatApiErrorDetail, IMAGES } from "@/lib/api";
import { toast } from "sonner";

const HOTSPOTS = [
  { id: "board", label: "Evidence Board", icon: FileStack, x: 20, y: 30, to: "/evidence", desc: "Open case files" },
  { id: "map", label: "World Map", icon: Map, x: 70, y: 33, to: "/evidence", desc: "Track investigations" },
  { id: "lamp", label: "Brain Scan Lab", icon: Search, x: 86, y: 56, to: "/brain-scan", desc: "Analyze any media" },
  { id: "typewriter", label: "Hall of Fame", icon: Trophy, x: 74, y: 63, to: "/hall-of-fame", desc: "Official investigators" },
  { id: "cabinet", label: "Your Case File", icon: FolderArchive, x: 10, y: 60, to: "/profile", desc: "Badges & profile" },
  { id: "academy", label: "Kids Academy", icon: GraduationCap, x: 23, y: 68, to: "/academy", desc: "Games & trivia for kids" },
  { id: "parents", label: "Parent Intelligence", icon: ShieldCheck, x: 66, y: 69, to: "/parents", desc: "Screen media for kids" },
];

function Hotspot({ spot, onNavigate }) {
  const Icon = spot.icon;
  return (
    <button
      data-testid={`hotspot-${spot.id}`}
      onClick={() => onNavigate(spot.to)}
      className="absolute -translate-x-1/2 -translate-y-1/2 group"
      style={{ left: `${spot.x}%`, top: `${spot.y}%` }}
    >
      <span className="relative flex items-center justify-center w-9 h-9 rounded-full hotspot-pulse"
        style={{ background: "rgba(245,158,11,0.18)", border: "1px solid rgba(245,158,11,0.6)" }}>
        <Icon size={16} className="text-[#F59E0B]" />
      </span>
      <span className="pointer-events-none absolute left-1/2 top-full mt-2 -translate-x-1/2 whitespace-nowrap rounded px-3 py-1.5 opacity-0 group-hover:opacity-100 transition-opacity"
        style={{ background: "rgba(10,7,5,0.92)", border: "1px solid rgba(245,158,11,0.3)" }}>
        <span className="block font-serif-hq text-base text-[#FDF6E3] leading-none">{spot.label}</span>
        <span className="block font-typewriter text-[10px] uppercase tracking-widest text-[#A89A80] mt-1">{spot.desc}</span>
      </span>
    </button>
  );
}

export default function MainHQ() {
  const navigate = useNavigate();
  const { user, checking, refresh } = useAuth();
  const [tvOpen, setTvOpen] = useState(false);
  const [report, setReport] = useState(null);
  const [claiming, setClaiming] = useState(false);

  useEffect(() => {
    document.title = "The Zombie Theory — Headquarters";
  }, []);

  const badges = user?.badges?.length || 0;
  const threshold = user?.unlock_threshold || 3;
  const unlocked = !!user?.tv_unlocked;

  const claim = async () => {
    setClaiming(true);
    try {
      const { data } = await api.post("/special-report/claim");
      setReport(data);
      await refresh();
      toast.success("Special Report broadcast confirmed.");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setClaiming(false);
    }
  };

  return (
    <div className="min-h-screen lamp-glow" style={{ background: "var(--deep-shadow)" }} data-testid="main-hq">
      {/* Top bar */}
      <div className="relative z-30 mx-auto max-w-7xl px-6 pt-7 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <img src={IMAGES.logo} alt="The Zombie Theory" className="h-16 w-16 object-contain rounded-md" style={{ filter: "drop-shadow(0 0 10px rgba(56,189,248,0.25))" }} />
          <div>
            <p className="font-typewriter text-[11px] uppercase tracking-[0.4em] text-[#38bdf8]">Investigative Headquarters</p>
            <h1 className="font-serif-hq text-2xl sm:text-3xl text-[#FDF6E3] leading-none mt-1">The Zombie Theory</h1>
          </div>
        </div>
        {checking ? null : user ? (
          <button data-testid="hq-profile-btn" onClick={() => navigate("/profile")}
            className="font-typewriter text-xs uppercase tracking-widest text-[#F59E0B] hover:text-[#FDF6E3] transition-colors">
            {user.investigator_number}
          </button>
        ) : (
          <button data-testid="hq-signin-btn" onClick={() => navigate("/enter")}
            className="rounded-full px-5 py-2 font-typewriter text-xs uppercase tracking-widest transition-colors"
            style={{ background: "#991B1B", color: "#FDF6E3" }}>
            Enter HQ
          </button>
        )}
      </div>

      {/* Tagline */}
      <div className="relative z-30 mx-auto max-w-7xl px-6 pt-6">
        <p className="font-serif-hq italic text-lg sm:text-xl text-[#E6D5B8]">
          "Your mind becomes what you consume."
        </p>
        <p className="text-sm text-[#A89A80] mt-1 max-w-xl">
          You are the investigator. Examine the room. Every object opens a new line of inquiry.
        </p>
      </div>

      {/* The Room */}
      <div className="relative mx-auto max-w-6xl px-6 py-8">
        <div className="relative overflow-hidden rounded-lg"
          style={{ boxShadow: "0 30px 80px rgba(0,0,0,0.8)", border: "1px solid rgba(245,158,11,0.15)" }}>
          <img src={IMAGES.room} alt="Detective Headquarters" className="w-full block select-none" draggable={false} />
          <div className="absolute inset-0" style={{ background: "radial-gradient(ellipse at 46% 38%, rgba(0,0,0,0) 30%, rgba(10,7,5,0.35) 100%)" }} />

          {HOTSPOTS.map((s) => (
            <Hotspot key={s.id} spot={s} onNavigate={navigate} />
          ))}

          {/* The TV hotspot */}
          <button
            data-testid="hotspot-tv"
            onClick={() => setTvOpen(true)}
            className="absolute -translate-x-1/2 -translate-y-1/2 group"
            style={{ left: "57%", top: "42%" }}
          >
            <span className={`relative flex items-center justify-center w-11 h-11 rounded-full ${unlocked ? "hotspot-pulse" : ""}`}
              style={{ background: unlocked ? "rgba(153,27,27,0.35)" : "rgba(10,7,5,0.5)", border: `1px solid ${unlocked ? "#991B1B" : "rgba(168,154,128,0.5)"}` }}>
              <Tv size={18} className={unlocked ? "text-[#FDF6E3]" : "text-[#A89A80]"} />
            </span>
            <span className="pointer-events-none absolute left-1/2 top-full mt-2 -translate-x-1/2 whitespace-nowrap rounded px-3 py-1.5 opacity-0 group-hover:opacity-100 transition-opacity"
              style={{ background: "rgba(10,7,5,0.92)", border: "1px solid rgba(245,158,11,0.3)" }}>
              <span className="block font-serif-hq text-base text-[#FDF6E3] leading-none">Headquarters TV</span>
              <span className="block font-typewriter text-[10px] uppercase tracking-widest text-[#A89A80] mt-1">
                {unlocked ? "Signal restored" : "No signal"}
              </span>
            </span>
          </button>
        </div>
      </div>

      {/* Directory fallback (mobile-friendly, always visible) */}
      <div className="relative z-30 mx-auto max-w-6xl px-6 pb-20">
        <p className="font-typewriter text-[11px] uppercase tracking-[0.35em] text-[#D97706] mb-4">Room Directory</p>
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
          {[...HOTSPOTS.filter((h) => h.id !== "map"), { id: "radio", label: "Radio (soon)", icon: Radio, to: null, desc: "Ambient" }].map((s) => {
            const Icon = s.icon;
            return (
              <button
                key={s.id}
                data-testid={`directory-${s.id}`}
                disabled={!s.to}
                onClick={() => s.to && navigate(s.to)}
                className="text-left rounded-md p-4 transition-colors disabled:opacity-40 hover:border-[#F59E0B]/50"
                style={{ background: "var(--wood-dark)", border: "1px solid var(--wood-medium)" }}
              >
                <Icon size={18} className="text-[#F59E0B] mb-3" />
                <div className="font-serif-hq text-lg text-[#FDF6E3] leading-none">{s.label}</div>
                <div className="font-typewriter text-[10px] uppercase tracking-widest text-[#A89A80] mt-1.5">{s.desc}</div>
              </button>
            );
          })}
        </div>
      </div>

      {/* TV Modal */}
      <AnimatePresence>
        {tvOpen && (
          <motion.div
            className="fixed inset-0 z-50 flex items-center justify-center p-4"
            style={{ background: "rgba(10,7,5,0.85)", backdropFilter: "blur(6px)" }}
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            data-testid="tv-modal"
          >
            <motion.div
              initial={{ scale: 0.94, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} exit={{ scale: 0.94, opacity: 0 }}
              className="relative w-full max-w-2xl rounded-lg overflow-hidden"
              style={{ background: "#0d0906", border: "2px solid #2C1A0F", boxShadow: "0 30px 90px rgba(0,0,0,0.9)" }}
            >
              <button data-testid="tv-close-btn" onClick={() => { setTvOpen(false); setReport(null); }}
                className="absolute top-3 right-3 z-10 text-[#A89A80] hover:text-[#FDF6E3] transition-colors">
                <X size={20} />
              </button>

              {/* Screen */}
              <div className="relative aspect-video flex items-center justify-center text-center px-8"
                style={{ background: report || unlocked ? "#08131c" : "#111" }}>
                {!report && !unlocked && <div className="absolute inset-0 static-tv" />}

                {report ? (
                  <div className="relative z-10">
                    <img src={IMAGES.logo} alt="The Zombie Theory" className="h-16 sm:h-20 mx-auto mb-3 object-contain" style={{ filter: "drop-shadow(0 0 16px rgba(56,189,248,0.45))" }} />
                    <div className="inline-block px-3 py-1 mb-4 font-typewriter text-[11px] uppercase tracking-[0.3em]"
                      style={{ background: "#991B1B", color: "#FDF6E3" }}>● Breaking Special Report</div>
                    <h2 className="font-serif-hq text-2xl sm:text-3xl text-[#FDF6E3] leading-tight">
                      {report.headline}
                    </h2>
                    <p className="mt-4 font-typewriter text-sm uppercase tracking-widest text-[#38bdf8]">
                      {report.name} · {report.investigator_number}
                    </p>
                  </div>
                ) : unlocked ? (
                  <div className="relative z-10">
                    <img src={IMAGES.logo} alt="The Zombie Theory" className="h-16 sm:h-20 mx-auto mb-3 object-contain" style={{ filter: "drop-shadow(0 0 16px rgba(56,189,248,0.45))" }} />
                    <p className="font-typewriter text-xs uppercase tracking-[0.3em] text-[#38bdf8] mb-2">Signal Restored</p>
                    <h2 className="font-serif-hq text-3xl text-[#FDF6E3]">A transmission is waiting.</h2>
                  </div>
                ) : (
                  <div className="relative z-10">
                    <p className="font-typewriter text-3xl sm:text-4xl tracking-[0.3em] text-[#E6D5B8]/80">NO SIGNAL</p>
                  </div>
                )}
              </div>

              {/* TV lower panel */}
              <div className="p-6" style={{ background: "var(--wood-dark)" }}>
                {report ? (
                  <div className="flex items-center gap-3">
                    <BadgeCheck className="text-[#F59E0B]" size={22} />
                    <p className="text-sm text-[#E6D5B8]">
                      You now hold an official Hall of Fame entry.{" "}
                      <button className="underline text-[#F59E0B]" onClick={() => navigate("/hall-of-fame")}>View the wall →</button>
                    </p>
                  </div>
                ) : !user ? (
                  <div className="flex items-center justify-between gap-4">
                    <p className="text-sm text-[#A89A80]">Sign in and complete your investigations to restore the broadcast.</p>
                    <button data-testid="tv-signin-btn" onClick={() => navigate("/enter")}
                      className="shrink-0 rounded-full px-5 py-2 font-typewriter text-xs uppercase tracking-widest"
                      style={{ background: "#991B1B", color: "#FDF6E3" }}>Enter HQ</button>
                  </div>
                ) : unlocked ? (
                  <button data-testid="tv-claim-btn" onClick={claim} disabled={claiming}
                    className="w-full rounded-md py-3 font-typewriter text-sm uppercase tracking-[0.25em] transition-colors disabled:opacity-60"
                    style={{ background: "#991B1B", color: "#FDF6E3" }}>
                    {claiming ? "Tuning in…" : "Play the Special Report"}
                  </button>
                ) : (
                  <div>
                    <div className="flex items-center justify-between mb-2">
                      <p className="font-typewriter text-[11px] uppercase tracking-widest text-[#A89A80]">Investigation Progress</p>
                      <p className="font-typewriter text-[11px] text-[#F59E0B]">{badges}/{threshold} badges</p>
                    </div>
                    <div className="h-2 rounded-full overflow-hidden" style={{ background: "#2C1A0F" }}>
                      <div className="h-full transition-all" style={{ width: `${Math.min(100, (badges / threshold) * 100)}%`, background: "#F59E0B" }} />
                    </div>
                    <p className="text-sm text-[#A89A80] mt-4">
                      Solve cases in the Evidence Room and run a Brain Scan to earn badges. At {threshold} badges the signal returns.
                    </p>
                    <div className="flex gap-3 mt-4">
                      <button onClick={() => navigate("/evidence")} className="flex-1 rounded-md py-2 font-typewriter text-xs uppercase tracking-widest hover:border-[#F59E0B]/60 transition-colors" style={{ background: "var(--deep-shadow)", border: "1px solid var(--wood-medium)", color: "#E6D5B8" }}>Evidence Room</button>
                      <button onClick={() => navigate("/brain-scan")} className="flex-1 rounded-md py-2 font-typewriter text-xs uppercase tracking-widest hover:border-[#F59E0B]/60 transition-colors" style={{ background: "var(--deep-shadow)", border: "1px solid var(--wood-medium)", color: "#E6D5B8" }}>Brain Scan Lab</button>
                    </div>
                  </div>
                )}
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
